import { Navigate, useLocation } from "react-router-dom";
import { useEffect, useRef, type ReactNode } from "react";
import { useSession } from "../../hooks/useSession";
import { useBrand } from "../brand/BrandProvider";
import { customersAdminApi } from "../../api/customersAdminApi";
import { isRouteAllowed, restrictedHome, type SessionKind } from "../../api/authApi";

/**
 * Wraps a route so anonymous users are redirected to /login while the session
 * is being refreshed. Keeps the return path so the login page can send users
 * back where they came from (future work).
 *
 * Pass `requireKind` to gate routes by a single session kind (e.g.
 * "Admin" for the brand admin page). Pass `requireAnyKind` to allow a
 * set of kinds (e.g. ["Admin", "Manager"] for the shared management
 * screens). When the kind doesn't match we render a plain 403 panel
 * rather than redirecting — prevents Admin-only links from sending
 * regular customers back to /login in a confusing loop.
 */
export function ProtectedRoute({
  children,
  requireKind,
  requireAnyKind,
}: {
  children: ReactNode;
  requireKind?: SessionKind;
  requireAnyKind?: SessionKind[];
}) {
  const { session, loading, setSession } = useSession();
  const { refresh: refreshBrand } = useBrand();
  const location = useLocation();

  // An admin or manager who is impersonating a customer and lands (e.g. via the
  // browser's Back button) on a page of their own kind is switched back
  // automatically instead of being shown "Not allowed".
  const allowedKindsForSwitch = requireKind ? [requireKind] : requireAnyKind;
  const needsSwitchBack =
    !loading &&
    !!session?.isAuthenticated &&
    !!allowedKindsForSwitch &&
    !allowedKindsForSwitch.includes(session.kind) &&
    !!session.originalKind &&
    allowedKindsForSwitch.includes(session.originalKind as SessionKind);
  const switchingBack = useRef(false);
  // Only when the *location* changes (Back button, a link) — never when the
  // session changes underneath the current page, which is exactly what happens
  // for a moment on the customers/managers pages right after "Switch to".
  const needsSwitchBackRef = useRef(needsSwitchBack);
  needsSwitchBackRef.current = needsSwitchBack;
  useEffect(() => {
    if (!needsSwitchBackRef.current || switchingBack.current) return;
    switchingBack.current = true;
    (async () => {
      try {
        const restored = await customersAdminApi.exitImpersonate();
        setSession(restored?.isAuthenticated ? restored : null);
        try {
          await refreshBrand();
        } catch {
          // cosmetic
        }
      } catch {
        // fall through: the "Not allowed" panel below stays visible
      } finally {
        switchingBack.current = false;
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  if (loading) {
    return (
      <div className="container" style={{ padding: "48px 0" }}>
        Loading…
      </div>
    );
  }

  if (!session?.isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  // A restricted login only ever sees its one page; every other route
  // (including /dashboard after login) lands there.
  const home = restrictedHome(session);
  if (home && location.pathname !== home) {
    return <Navigate to={home} replace />;
  }
  // A menu-restricted login (e.g. BasicMenu) lands on the dashboard for
  // every route outside its allowed sections.
  if (!isRouteAllowed(session, location.pathname)) {
    return <Navigate to="/dashboard" replace />;
  }

  // `requireKind` (single) takes precedence for backwards compatibility;
  // `requireAnyKind` (set) gates the shared admin/manager screens.
  const allowedKinds = requireKind
    ? [requireKind]
    : requireAnyKind;
  if (allowedKinds && !allowedKinds.includes(session.kind)) {
    if (needsSwitchBack) {
      return (
        <div className="container" style={{ padding: "48px 0" }}>
          Switching back…
        </div>
      );
    }
    return (
      <div className="container" style={{ padding: "48px 0" }}>
        <div className="tile">
          <h2 style={{ marginTop: 0 }}>Not allowed</h2>
          <p>You don&apos;t have permission to view this page.</p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
